/**
 * What that icon is, when you press one.
 *
 * A custom emoji in a message is a picture with no name on it. You can see
 * somebody used one and you cannot find out what it is called, whether you
 * are allowed it, or how to type it - the only route was the picker, and only
 * if you already knew what you were looking for.
 *
 * So pressing one opens a small card: the picture, what it is called, who may
 * use it, and one button that puts `:name:` on the clipboard. Nothing you
 * cannot get from the picker, right where you wondered about it.
 *
 * Opened from anywhere through openEmojiInfo, drawn in one place, and on the
 * shared layer stack - so a press anywhere else puts it away, and pressing
 * the same icon twice is a toggle.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fileUrl } from '@/lib/api';
import { cn } from '@/lib/cn';
import { copyText } from '@/lib/clipboard';
import { useLayer } from '@/lib/layers';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import type { CustomEmoji } from '@/lib/emoji';

/** Who each one is for, in the words the staff console uses. */
const WHO: Record<string, string> = {
  everyone: 'Anyone on Pugo can use this one.',
  premium: 'Premium members and above.',
  vip: 'VIP members only.',
  staff: 'Pugo staff only.'
};

const WHO_SHORT: Record<string, string> = {
  everyone: 'Everyone',
  premium: 'Premium',
  vip: 'VIP',
  staff: 'Staff'
};

type State = { emoji: CustomEmoji; owner: HTMLElement } | null;

const watchers = new Set<(next: State) => void>();
let current: State = null;

function set(next: State): void {
  current = next;
  for (const fn of watchers) fn(next);
}

/** Show what this icon is. A second press on the same one puts it away. */
export function openEmojiInfo(emoji: CustomEmoji, owner: HTMLElement): void {
  if (current && current.owner === owner) { set(null); return; }
  set({ emoji, owner });
}

export function closeEmojiInfo(): void { set(null); }

export function EmojiInfoHost() {
  const [state, setState] = useState<State>(current);
  const box = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  /* The list is the live one: an icon can be withdrawn or have its access
     changed while this is open, and the card should say so. */
  const custom = useSession((s) => s.emoji);

  useEffect(() => {
    watchers.add(setState);
    return () => { watchers.delete(setState); };
  }, []);

  useLayer(!!state, {
    node: () => box.current,
    /* The icon that opened it does its own toggling, so closing here first
       would let that press open it straight back up. */
    keep: (t) => !!state?.owner.contains(t),
    close: () => set(null)
  });

  useEffect(() => { setCopied(false); }, [state]);

  if (!state) return null;

  /* Prefer the live row, fall back to what was passed in. */
  const emoji = custom.find((e) => e.id === state.emoji.id) ?? state.emoji;
  const token = ':' + emoji.name + ':';
  const access = emoji.access ?? 'everyone';
  const locked = emoji.usable === false;

  /* Beside the icon, flipped to whichever side has room, and never off the
     top or the bottom. */
  const r = state.owner.isConnected ? state.owner.getBoundingClientRect() : null;
  const width = 250;
  const margin = 10;
  let left = r ? r.left + r.width / 2 - width / 2 : margin;
  left = Math.min(Math.max(margin, left), Math.max(margin, window.innerWidth - width - margin));
  const below = r ? r.bottom + 8 : margin;
  const wantsAbove = r ? below + 168 > window.innerHeight - margin : false;
  const top = r ? (wantsAbove ? Math.max(margin, r.top - 168 - 8) : below) : margin;

  return createPortal(
    <div
      ref={box}
      role="dialog"
      aria-label={token}
      style={{ left, top, width }}
      className={cn(
        'fixed z-[450] flex flex-col gap-2.5 rounded-[calc(16px*var(--radius-mul,1))] border border-line-soft',
        'bg-surface-1 p-3 shadow-[var(--shadow-3)]',
        'animate-[fade-in_160ms_var(--ease-out)] motion-reduce:animate-none'
      )}
    >
      <div className="flex items-center gap-3">
        <img
          src={fileUrl(emoji.url)}
          alt={token}
          className="h-11 w-11 flex-none object-contain"
        />
        <div className="min-w-0 flex-1">
          <p className="m-0 truncate text-[14px] font-bold text-ink">{token}</p>
          <p className="m-0 flex items-center gap-1.5 text-[11.5px] font-semibold text-mute">
            <Icon name={locked ? 'lock' : 'check'} size={11} className={locked ? 'text-warn' : 'text-good'} />
            {WHO_SHORT[access] ?? 'Everyone'}
            {emoji.animated ? <span className="text-ghost">· animated</span> : null}
          </p>
        </div>
      </div>

      <p className="m-0 text-[12px] leading-[1.45] text-dim">
        {WHO[access] ?? WHO.everyone}
        {locked ? ' You cannot use this one yet.' : ''}
      </p>

      <button
        type="button"
        onClick={() => { void copyText(token).then((ok) => setCopied(ok)); }}
        className={cn(
          'flex items-center justify-center gap-1.5 rounded-[calc(10px*var(--radius-mul,1))] px-3 py-1.5',
          'text-[12.5px] font-bold outline-none transition-[background-color,color] duration-[var(--t-hover)]',
          'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink',
          'focus-visible:shadow-[0_0_0_2px_var(--accent)]'
        )}
      >
        <Icon name={copied ? 'check' : 'copy'} size={13} />
        {copied ? 'Copied ' + token : 'Copy ' + token}
      </button>
    </div>,
    document.body
  );
}
